<template>
  <HKLModal
    ref="modal"
    :closable="true"
    :afterClose="$attrs.afterClose"
    :hasOkBtn="false"
    :hasCancelBtn="false"
    :width="'900px'"
    :dialogStyle="{'padding-top':'40px', 'margin-top': '0%', 'top':'0px'}"
  >
    <HKLForm
      ref="formRef"
      :canEdit="canEdit"
    >
      <HKLFormItem :label="$t('subject')">
        {{ subject }}
      </HKLFormItem>
      <HKLFormItem
        class="to text-input"
        :label="$t('to')"
        :required="true"
      >
        <HKLInputEmails
          v-model="formData.to"
          v-hkldinput
          v-autosize
          :checkerOptions="{ required: true }"
        />
        {{ formData.to || $t('emptyValue') }}
      </HKLFormItem>
      <HKLFormItem
        class="cc text-input"
        :label="$t('cc')"
      >
        <HKLInputEmails
          v-model="formData.cc"
          v-hkldinput
          v-autosize
        />
        {{ formData.cc || $t('emptyValue') }}
      </HKLFormItem>
      <HKLFormItem
        class="bcc text-input"
        v-if="!isTenant"
        :label="$t('bcc')"
      >
        <HKLInputEmails
          v-model="formData.bcc"
          v-hkldinput
          v-autosize
        />
        {{ formData.bcc || $t('emptyValue') }}
      </HKLFormItem>
      <div>
        <img
          v-region-img-src="'email-logo-hkland.png'"
          alt="Hongkong Land"
          class="hkland-logo"
        >
      </div>
      <div>
        {{ $t('tenantProjects.inspection.dearAll') }}
      </div>
      <HKLFormItem>
        <DescriptionTinymceEditor
          ref="descriptionRef"
          v-model="formData.content"
          v-fusioninputcheck="{
            type: 'string',
            required: true,
          }"
        />
        <div v-html="formData.content" />
      </HKLFormItem>
      <HKLFormItem :label="$t('attachments')">
        <ul
          v-if="validAttachments.length"
          class="attachments"
        >
          <li
            v-for="(item, index) of validAttachments"
            :key="index"
          >
            {{ item.filename }}
          </li>
        </ul>
        <template v-else>
          {{ $t('emptyValue') }}
        </template>
      </HKLFormItem>
    </HKLForm>
    <template
      v-if="canEdit"
      #footer
    >
      <HKLButton
        class="btn"
        color="darkblue"
        action="save"
        @click="onSave"
      />
      <HKLButton
        class="btn"
        color="orange"
        action="send"
        @click="onSend"
      />
    </template>
  </HKLModal>
</template>

<script>
import { ref, unref, nextTick, onUnmounted, computed } from 'vue-demi'
import lodash from 'lodash'
import HKLInputEmails from '@/controls/Input/HKLInputEmails'
import HKLButton from '@/controls/Button/HKLButton'
import HKLModal from '@/controls/Modal/HKLModal'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { useRefForm } from '@/controls/Form/hook'
import HKLForm from '@/controls/Form/HKLForm'
import HKLFormItem from '@/controls/Form/HKLFormItem'
import { useAttachments, useCurrentInspection } from './Provider'
import { createEmailHistoriesById, sendEmailHistoriesById, updateEmailHistoriesById } from '@/api/inspection/request'
import { useStoreGetters } from '@/store'
import i18n from '@/utils/i18n'
import { SEND_STATUS } from './consts'
import DescriptionTinymceEditor from '../../DescriptionTinymceEditor.component'
import { utils as formUtils } from '@/plugin/InputChange'
import { contactTypes } from '@/api/users/consts'
import { getMainContractorEmail } from '@/api/postman/tenantProject'
import { useSubTenantUsers } from './hooks'
import { processDuplicateReceivers } from '@/api/postman/util'
import notification from '@/utils/notification'

export default {
  name: 'InspectionSendModal',
  components: {
    HKLInputEmails,
    HKLButton,
    HKLModal,
    HKLForm,
    HKLFormItem,
    DescriptionTinymceEditor,
  },
  setup () {
    const modalRef = useRefModal()
    const formRef = useRefForm()
    const inspection = useCurrentInspection()
    const attachments = useAttachments()
    const { isTenant, currentUser } = useStoreGetters('user', ['isTenant', 'currentUser'])
    const { currentTenantProject } = useStoreGetters('tenantProject', ['currentTenantProject'])
    const { currentTenancy } = useStoreGetters('ccd', ['currentTenancy'])
    const subTenantUsers = useSubTenantUsers(currentTenancy)

    const formData = ref({
      to: '',
      cc: '',
      bcc: '',
      content: '',
      status: SEND_STATUS.UNKNOWN,
    })

    const projectInfo = computed(() => {
      const project = unref(currentTenantProject)
      const tenancy = unref(currentTenancy)
      const projectType = lodash.get(project, 'type')
      const building = lodash.get(tenancy, 'building.fullName', i18n.t('emptyValue'))
      let projectLocation = lodash.get(project, 'projectLocation', i18n.t('emptyValue'))
      projectLocation = Array.isArray(projectLocation) ? projectLocation.join(', ') : projectLocation
      const companyName = lodash.get(tenancy, 'company.name', i18n.t('emptyValue'))
      const type = projectType ? i18n.t(`tenancyProject.${lodash.toLower(projectType)}`) : i18n.t('emptyValue')

      return [type, building, projectLocation, companyName]
    })

    const mailId = computed(() => unref(formData).id)
    const subject = computed(() => {
      if (unref(formData).status === SEND_STATUS.SENT) return unref(formData).subject
      return i18n.t('tenantProjects.inspection.subject', unref(projectInfo))
    })
    const validAttachments = computed(() => {
      const uploadedAttachmentList = unref(attachments)
      const attachmentList = unref(formData).status === SEND_STATUS.SENT
        ? unref(formData).attachmentList.map((id) => uploadedAttachmentList.find((item) => item.id === id))
        : uploadedAttachmentList.filter((item) => item.metaData.viewInEmail)
      return attachmentList
    })
    const canEdit = computed(() => unref(formData).status === SEND_STATUS.DRAFT)

    const openModal = (obj) => {
      modalRef.open()
      const initContent = i18n.t('tenantProjects.inspection.initContent', unref(projectInfo))
      const tenancy = unref(currentTenancy)
      let tenancyMasterEmail = ''
      try {
        const user = lodash.get(tenancy, ['accounts', 0])
        tenancyMasterEmail = user.username
        tenancyMasterEmail = user.contacts.find((c) => c.type === contactTypes.EMAIL).value
      } catch (e) {
      }
      const subTenantUserEmails = unref(subTenantUsers).map((user) => {
        const contact = user.contacts.find((c) => c.type === contactTypes.EMAIL)
        return (contact && contact.value) || user.username
      })
      const mainContractorEmail = getMainContractorEmail()
      const to = [
        tenancyMasterEmail,
        ...subTenantUserEmails,
        mainContractorEmail,
      ].filter(Boolean)
      let cc = unref(currentUser).username
      try {
        cc = unref(currentUser).contacts.find((c) => c.type === contactTypes.EMAIL).value
      } catch (e) {
      }
      return nextTick(() => {
        formData.value = obj || {
          to: [...new Set(to)].join(';'),
          cc,
          bcc: '',
          content: `<div>${initContent}</div>`,
          status: SEND_STATUS.DRAFT,
          attachmentList: [],
        }
      })
    }
    const onSave = async () => {
      const emailRecipients = lodash.pick(unref(formData), ['to', 'cc', 'bcc'])
      const formatRecipients = processDuplicateReceivers(emailRecipients)
      const newData = {
        ...formatRecipients,
        content: unref(formData).content,
        subject: unref(subject),
      }
      if (!unref(mailId)) {
        formData.value = await createEmailHistoriesById(unref(inspection).id, newData)
      } else {
        await updateEmailHistoriesById(unref(mailId), newData)
      }
      formUtils.resetDirty()
      notification.fireNotification(true, i18n.t('success'))
    }
    const onSend = async () => {
      if (!(formRef.validate())) return false
      await onSave()
      await sendEmailHistoriesById(unref(mailId))
      formUtils.resetDirty()
      modalRef.close()
    }

    onUnmounted(() => {
      formData.value.content = ''
    })

    return {
      ...modalRef,
      open: openModal,
      formRef: formRef.form,
      formData,
      subject,
      canEdit,
      validAttachments,
      isTenant,
      onSave,
      onSend,
    }
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/textField';
.btn {
  margin-right: 5px;
  &:last-child {
    margin-right: 0;
  }
}
.hkland-logo {
  margin-top: 20px;
  margin-bottom: 20px;
  height: 40px;
}
.hkl-form.read-only {
  .to, .cc, .bcc {
    ::v-deep {
      label {
        margin-top: 0;
        margin-bottom: 0;
        font-weight: normal;
      }
      label::after {
        content: ':';
      }
      label, label + div {
        display: inline-block;
      }
    }
  }
  .to {
    margin-top: 10px;
  }
  .bcc {
    margin-bottom: 10px;
  }
}
ul.attachments {
  list-style-type: decimal;
}
</style>
